import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import {
    chatColors,
    chatMenuItemSx,
    chatMenuLabelSx,
    chatPopoverPaperSx,
    chatRadii
} from './chatTheme';

// Slides ascending, whatever order the room page happens to sort them in.
const byNumber = (slides) => [...slides].sort((a, b) => Number(a.index) - Number(b.index));

/**
 * The label on the scope button: "Room" when nothing is picked, the single
 * selection, or "Slide 1 +2" for several. Question sorts after the slides, so a
 * mixed selection still leads with the lowest slide.
 */
export function scopeButtonLabel(scope, slides = []) {
    const selectedIds = (scope && scope.slideIds) || [];
    const labels = byNumber(slides)
        .filter((slide) => selectedIds.includes(slide.itemId))
        .map((slide) => `Slide ${slide.index}`);
    if (scope && scope.question) {
        labels.push('Question');
    }
    if (labels.length === 0) {
        return 'Room';
    }
    if (labels.length === 1) {
        return labels[0];
    }
    return `${labels[0]} +${labels.length - 1}`;
}

function Checkbox({ checked }) {
    return (
        <Box
            sx={(theme) => ({
                flex: '0 0 auto',
                width: 15,
                height: 15,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                border: `1px solid ${checked ? chatColors(theme).primary : chatColors(theme).border}`,
                backgroundColor: checked ? chatColors(theme).primary : 'transparent'
            })}>
            {checked && <CheckIcon sx={{ fontSize: 12, color: 'common.white' }} />}
        </Box>
    );
}

/**
 * The scope picker inside the composer's input shell: which slides (and/or
 * "Question") the next chat is posted to. Only live slides are offered - a
 * deleted slide can still be filtered on, but nothing new can be tagged to it.
 */
export default function ({ scope, slides = [], open, anchorEl, onOpen, onClose, onChange, disabled = false }) {
    const selectedIds = scope.slideIds;
    const orderedSlides = byNumber(slides);

    const toggleSlide = (itemId) => {
        onChange({
            ...scope,
            slideIds: selectedIds.includes(itemId)
                ? selectedIds.filter((id) => id !== itemId)
                : selectedIds.concat(itemId)
        });
    };

    return (
        <>
            <ButtonBase
                disabled={disabled}
                aria-label='Choose which slides this chat is about'
                aria-haspopup='true'
                onClick={onOpen}
                sx={(theme) => ({
                    // Sized to its label and never shrunk: as a flex sibling of
                    // the (fullWidth) input it would otherwise be ellipsised
                    // down to "R..." even with room to spare. maxWidth caps the
                    // longest labels instead.
                    flex: '0 0 auto',
                    maxWidth: 148,
                    gap: '2px',
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 10px',
                    borderRadius: chatRadii.action,
                    color: chatColors(theme).text,
                    '&.Mui-disabled': { color: theme.palette.text.disabled },
                    '&:hover': { backgroundColor: chatColors(theme).hoverSurface }
                })}>
                <Box
                    component='span'
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {scopeButtonLabel(scope, slides)}
                </Box>
                <ArrowDropDownIcon sx={{ flex: '0 0 auto', fontSize: 18 }} />
            </ButtonBase>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: chatPopoverPaperSx }}>
                <Typography sx={chatMenuLabelSx}>ADD THIS CHAT TO A SLIDE</Typography>
                <ButtonBase
                    onClick={() => onChange({ slideIds: [], question: false })}
                    sx={chatMenuItemSx}>
                    <Checkbox checked={selectedIds.length === 0 && !scope.question} />
                    <Box component='span'>Room</Box>
                </ButtonBase>
                {orderedSlides.map((slide) => (
                    <ButtonBase
                        key={slide.itemId}
                        onClick={() => toggleSlide(slide.itemId)}
                        sx={chatMenuItemSx}>
                        <Checkbox checked={selectedIds.includes(slide.itemId)} />
                        <Box component='span'>{`Slide ${slide.index}`}</Box>
                    </ButtonBase>
                ))}
                <ButtonBase
                    onClick={() => onChange({ ...scope, question: !scope.question })}
                    sx={chatMenuItemSx}>
                    <Checkbox checked={scope.question} />
                    <Box component='span'>Question</Box>
                </ButtonBase>
            </Popover>
        </>
    );
}
